import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {fmtThreeCommas, useFormatWithSettings} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import {isEmpty} from 'lodash';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {green, purple, red, yellow} from '@mui/material/colors';

import {calculatePercent} from '../logics';
import {SeriesType} from '../types';

const DISPLAY_INFO: Record<SeriesType, Record<string, any>> = {
  [SeriesType.NG]: {name: 'NG', color: red[500]},
  [SeriesType.WARNING]: {name: 'WARNING', color: yellow[500]},
  [SeriesType.OK]: {name: 'OK', color: green[500]},
  [SeriesType.OVER]: {name: 'OVER', color: purple[500]}
};

/**
 * 引数
 */
interface MatchingRatePieProps {
  title: string;
  source: Record<string, any>[];
  exportFilename?: string;
  height?: string;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;

  onClick?: (prm: {data: any}) => void;
}

interface SeriesData {
  value: number; // par
  name: string;
  map: number;
  sum_allpoint: number;
  sum_okpoint: number;
  sum_ngpoint: number;
  sum_warningpoint: number;
  sum_overpoint: number;
  sum_okpar: number;
  sum_ngpar: number;
  sum_warningpar: number;
  sum_overpar: number;
  itemStyle: Record<string, any>;
}

interface Series {
  name: string;
  type: string;
  radius: string;
  data: SeriesData[];
}

/**
 * MatchingRatePie
 * @param props
 * @returns
 */
export const MatchingRatePie = (props: MatchingRatePieProps) => {
  const {t} = useTranslation();
  const {fmtPercent} = useFormatWithSettings();
  const {parsePercent} = useParseWithSettings();

  const [series, setSeries] = useState<Series>();
  const [exportData, setExportData] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    if (isEmpty(props.source)) return;

    //集約する
    const sums = props.source.reduce(
      (acc, r) => {
        return {
          map: r.map,
          sum_allpoint: acc.sum_allpoint + Number(r.sum_allpoint),
          [SeriesType.OK]: acc[SeriesType.OK] + Number(r[SeriesType.OK]),
          [SeriesType.NG]: acc[SeriesType.NG] + Number(r[SeriesType.NG]),
          [SeriesType.WARNING]: acc[SeriesType.WARNING] + Number(r[SeriesType.WARNING]),
          [SeriesType.OVER]: acc[SeriesType.OVER] + Number(r[SeriesType.OVER])
        };
      },
      {
        map: props.source[0]?.map,
        sum_allpoint: 0,
        [SeriesType.OK]: 0,
        [SeriesType.NG]: 0,
        [SeriesType.WARNING]: 0,
        [SeriesType.OVER]: 0
      }
    );

    setSeries({
      name: '',
      type: 'pie',
      radius: '70%',
      data: Object.entries(DISPLAY_INFO).map(([key, displayInfo]) => {
        const perInfo: Record<string, number> = {
          [SeriesType.OK]: parsePercent(calculatePercent(sums[SeriesType.OK], sums.sum_allpoint)),
          [SeriesType.NG]: parsePercent(calculatePercent(sums[SeriesType.NG], sums.sum_allpoint)),
          [SeriesType.WARNING]: parsePercent(
            calculatePercent(sums[SeriesType.WARNING], sums.sum_allpoint)
          ),
          [SeriesType.OVER]: parsePercent(
            calculatePercent(sums[SeriesType.OVER], sums.sum_allpoint)
          )
        };

        return {
          value: perInfo[key],
          name: displayInfo.name,
          map: sums.map,
          sum_allpoint: sums.sum_allpoint,
          sum_okpoint: sums[SeriesType.OK],
          sum_ngpoint: sums[SeriesType.NG],
          sum_warningpoint: sums[SeriesType.WARNING],
          sum_overpoint: sums[SeriesType.OVER],
          sum_okpar: perInfo[SeriesType.OK],
          sum_ngpar: perInfo[SeriesType.NG],
          sum_warningpar: perInfo[SeriesType.WARNING],
          sum_overpar: perInfo[SeriesType.OVER],
          itemStyle: {
            color: displayInfo.color
          }
          // label: {position: 'inside'}
        };
      })
    });
  }, [props.source]);

  useEffect(() => {
    if (series == null) return;

    const rowSeriesData = series.data.find(s => s.name === DISPLAY_INFO[SeriesType.OK].name);
    if (!rowSeriesData) return;

    setExportData([
      {
        検査部位: rowSeriesData.map,
        合計点数: rowSeriesData.sum_allpoint,
        ＮＧ比率: rowSeriesData.sum_ngpar,
        ＮＧ点数: rowSeriesData.sum_ngpoint,
        ＷＡＲＮＩＮＧ比率: rowSeriesData.sum_warningpar,
        ＷＡＲＮＩＮＧ点数: rowSeriesData.sum_warningpoint,
        ＯＫ比率: rowSeriesData.sum_okpar,
        ＯＫ点数: rowSeriesData.sum_okpoint,
        ＯＶＥＲ比率: rowSeriesData.sum_overpar,
        ＯＶＥＲ点数: rowSeriesData.sum_overpoint
      }
    ]);
  }, [series]);

  return (
    <LFCBaseCharts
      option={{
        title: {
          text: props.title
        },
        tooltip: {
          show: true,
          formatter: (params: any) => {
            const dinfo = Object.values(DISPLAY_INFO).find(r => r.name === params.name);
            const borderColor = dinfo?.color;
            const icon = borderColor ? `<span style="color:${borderColor}">●</span> ` : '';

            return `${icon}${params.name}<br/>
              ${fmtPercent(params.value)}<br/>
              <br/>
              <span style="font-weight:bold">${t('合計点群')}</span>: ${fmtThreeCommas(
                params.data.sum_allpoint
              )}点<br/>
              &nbsp;&nbsp;<span style="font-weight:bold">${t('NG')}</span>: ${fmtPercent(
                params.data.sum_ngpar
              )} (${fmtThreeCommas(params.data.sum_ngpoint)}点)<br/>
              &nbsp;&nbsp;<span style="font-weight:bold">${t('WARNING')}</span>: ${fmtPercent(
                params.data.sum_warningpar
              )} (${fmtThreeCommas(params.data.sum_warningpoint)}点)<br/>
              &nbsp;&nbsp;<span style="font-weight:bold">${t('OK')}</span>: ${fmtPercent(
                params.data.sum_okpar
              )} (${fmtThreeCommas(params.data.sum_okpoint)}点)<br/>
              &nbsp;&nbsp;<span style="font-weight:bold">${t('OVER')}</span>: ${fmtPercent(
                params.data.sum_overpar
              )} (${fmtThreeCommas(params.data.sum_overpoint)}点)<br/>
              `;
          }
        },
        dataZoom: [
          {
            type: 'inside'
          }
        ],
        series: series
      }}
      exportData={exportData}
      exportFields={{
        検査部位: t('検査部位'),
        合計点数: t('合計点数'),
        ＮＧ比率: t('ＮＧ比率(%)'),
        ＮＧ点数: t('ＮＧ点数'),
        ＷＡＲＮＩＮＧ比率: t('ＷＡＲＮＩＮＧ比率(%)'),
        ＷＡＲＮＩＮＧ点数: t('ＷＡＲＮＩＮＧ点数'),
        ＯＫ比率: t('ＯＫ比率(%)'),
        ＯＫ点数: t('ＯＫ点数'),
        ＯＶＥＲ比率: t('ＯＶＥＲ比率(%)'),
        ＯＶＥＲ点数: t('ＯＶＥＲ点数')
      }}
      exportFilename={props.exportFilename}
      height={props.height ? props.height : '100px'}
      onEvents={{
        click: (params: any) => {
          if (props.onClick != null) {
            props.onClick(params);
          }
        }
      }}
      zoomCharts={props.zoomCharts ? props.zoomCharts : undefined}
      zoomChartsClose={props.zoomChartsClose ? props.zoomChartsClose : undefined}
      zoomChartId={props.zoomChartId ? props.zoomChartId : undefined}
      zoomStatus={props.zoomChartId === props.zoomedChartId}
    />
  );
};
